import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { PurgeUsersButton } from "@/components/PurgeUsersButton";
import { CheckCircle2, XCircle, Clock, DollarSign, Users, Search, TrendingUp, PlusCircle, Film, Loader2, RefreshCw } from "lucide-react";

const STATUS_LABEL = {
  pending_review: { label: "Pendente", color: "#FFCE00", icon: <Clock size={14}/> },
  confirmed: { label: "Confirmado", color: "#CCFF00", icon: <CheckCircle2 size={14}/> },
  rejected: { label: "Rejeitado", color: "#FF0055", icon: <XCircle size={14}/> },
};

export default function Admin() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [metrics, setMetrics] = useState(null);
  const [pix, setPix] = useState([]);
  const [statusFilter, setStatusFilter] = useState("pending_review");
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState(null);
  const [reasonInput, setReasonInput] = useState({});
  const [videos, setVideos] = useState([]);
  const [videoBusy, setVideoBusy] = useState(false);
  const [users, setUsers] = useState([]);
  const [userFilter, setUserFilter] = useState("all");

  useEffect(() => {
    if (loading) return;
    if (!user) { nav("/login"); return; }
    if (!user.is_admin) { toast.error("Acesso restrito"); nav("/dashboard"); return; }
    loadAll();
  }, [user, loading, nav]);

  const loadAll = async () => {
    // Resilient parallel load: a failure in ONE endpoint shouldn't blank the whole admin UI
    const [m, p, v, u] = await Promise.allSettled([
      api.get("/admin/metrics"),
      api.get(`/admin/pix?status=${statusFilter}&search=${encodeURIComponent(search)}`),
      api.get("/admin/courses/intro-videos"),
      api.get(`/admin/users?status=${userFilter}&search=${encodeURIComponent(search)}`),
    ]);
    if (m.status === "fulfilled") setMetrics(m.value.data);
    else console.warn("[Admin] metrics load failed:", m.reason?.message);
    if (p.status === "fulfilled") setPix(p.value.data || []);
    else { console.warn("[Admin] pix load failed:", p.reason?.message); toast.error("Erro ao listar pagamentos Pix"); }
    if (v.status === "fulfilled") setVideos(v.value.data.videos || []);
    else console.warn("[Admin] videos load failed:", v.reason?.message);
    if (u.status === "fulfilled") setUsers(u.value.data || []);
    else console.warn("[Admin] users load failed:", u.reason?.message);
  };

  const generateAllVideos = async () => {
    setVideoBusy(true);
    try {
      const { data } = await api.post("/admin/courses/intro-videos/generate-all");
      if (data.count > 0) toast.success(`${data.count} vídeo(s) na fila. Cada um leva 2-5 min.`);
      else toast.info("Todos os vídeos já foram gerados ou estão em progresso.");
      loadAll();
    } catch { toast.error("Erro ao enfileirar vídeos"); }
    finally { setVideoBusy(false); }
  };

  useEffect(() => { if (user?.is_admin) loadAll(); /* eslint-disable-next-line */ }, [statusFilter, userFilter]);

  // Auto-refresh: pulls new pending Pix every 30s + on window focus
  useEffect(() => {
    if (!user?.is_admin) return;
    const interval = setInterval(() => { loadAll(); }, 30000);
    const onFocus = () => { loadAll(); };
    window.addEventListener("focus", onFocus);
    return () => { clearInterval(interval); window.removeEventListener("focus", onFocus); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.is_admin, statusFilter, search]);

  const confirmPix = async (id) => {
    setBusy(id);
    try {
      await api.post(`/admin/pix/${id}/confirm`);
      toast.success("Pix confirmado! Assinatura mantida por 30 dias.");
      loadAll();
    } catch { toast.error("Erro ao confirmar"); }
    finally { setBusy(null); }
  };

  const rejectPix = async (id) => {
    setBusy(id);
    try {
      const reason = reasonInput[id] || "";
      await api.post(`/admin/pix/${id}/reject`, { reason });
      toast.success("Pix rejeitado. Aluno notificado por email.");
      setReasonInput({ ...reasonInput, [id]: "" });
      loadAll();
    } catch { toast.error("Erro ao rejeitar"); }
    finally { setBusy(null); }
  };

  const extend = async (id) => {
    setBusy(id);
    try {
      await api.post(`/admin/pix/${id}/extend`, { days: 30 });
      toast.success("+30 dias adicionados!");
      loadAll();
    } catch { toast.error("Erro ao estender"); }
    finally { setBusy(null); }
  };

  if (!user || !user.is_admin) return null;

  return (
    <div data-testid="admin-page" className="grid-bg min-h-[85vh]">
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-10">
        <span className="sticker">PAINEL ADMIN</span>
        <h1 className="mt-6 font-bebas text-6xl md:text-7xl tracking-tighter">
          COMANDO <span className="text-[#CCFF00]">CRIA</span>
        </h1>

        {/* Metrics */}
        {metrics && (
          <div data-testid="admin-metrics" className="mt-10 grid grid-cols-2 md:grid-cols-6 gap-4">
            <div className="brutal-card" style={{padding:'1rem'}}>
              <Clock className="text-[#FFCE00]" size={20}/>
              <div className="font-bebas text-4xl mt-2">{metrics.pix_pending}</div>
              <div className="font-manrope text-xs text-white/60">Pix pendentes</div>
            </div>
            <div className="brutal-card" style={{padding:'1rem'}}>
              <CheckCircle2 className="text-[#CCFF00]" size={20}/>
              <div className="font-bebas text-4xl mt-2">{metrics.pix_confirmed}</div>
              <div className="font-manrope text-xs text-white/60">Pix confirmados</div>
            </div>
            <div className="brutal-card" style={{padding:'1rem'}}>
              <DollarSign className="text-[#CCFF00]" size={20}/>
              <div className="font-bebas text-3xl mt-2">R$ {metrics.month_revenue.toFixed(2).replace('.',',')}</div>
              <div className="font-manrope text-xs text-white/60">Faturamento mês</div>
            </div>
            <div className="brutal-card" style={{padding:'1rem'}}>
              <Users className="text-[#00F0FF]" size={20}/>
              <div className="font-bebas text-4xl mt-2">{metrics.active_subs}</div>
              <div className="font-manrope text-xs text-white/60">Assinantes ativos</div>
            </div>
            <div className="brutal-card" style={{padding:'1rem'}}>
              <Users className="text-white/60" size={20}/>
              <div className="font-bebas text-4xl mt-2">{metrics.total_users}</div>
              <div className="font-manrope text-xs text-white/60">Total de contas</div>
            </div>
            <div className="brutal-card" style={{padding:'1rem'}}>
              <TrendingUp className="text-[#FF0055]" size={20}/>
              <div className="font-bebas text-4xl mt-2">{metrics.today_pix}</div>
              <div className="font-manrope text-xs text-white/60">Pix hoje</div>
            </div>
          </div>
        )}

        {/* All Users */}
        <div className="mt-10">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="font-bebas text-3xl tracking-tighter">USUÁRIOS CADASTRADOS <span className="text-white/40 text-xl">({users.length})</span></div>
              <div className="font-manrope text-xs text-white/50">Toda pessoa que criou conta — paga ou não</div>
              <div className="mt-3"><PurgeUsersButton onDone={loadAll}/></div>
            </div>
            <div className="flex items-center gap-2">
              {["all","active","pending","inactive"].map(s => (
                <button
                  key={s}
                  data-testid={`user-filter-${s}`}
                  onClick={()=>setUserFilter(s)}
                  className={`px-3 py-1.5 font-manrope text-xs uppercase tracking-widest border ${userFilter===s ? "bg-[#D4FF00] text-black border-[#D4FF00]" : "text-white/70 border-white/20 hover:border-white"}`}
                >
                  {s === "all" ? "Todos" : s === "active" ? "Pagando" : s === "pending" ? "Pendente" : "Grátis"}
                </button>
              ))}
            </div>
          </div>
          <div className="mt-4 brutal-card overflow-x-auto" style={{padding:0}}>
            <table className="w-full font-manrope text-sm">
              <thead>
                <tr className="text-left text-white/50 text-xs uppercase tracking-widest">
                  <th className="px-4 py-3">Cadastrado</th>
                  <th className="px-4 py-3">Nome / Email</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Admin?</th>
                </tr>
              </thead>
              <tbody>
                {users.length === 0 && (
                  <tr><td colSpan={4} className="px-4 py-6 text-center text-white/40">Nenhum usuário</td></tr>
                )}
                {users.map(u => {
                  const status = u.is_admin ? {label:"ADMIN", c:"#D4FF00"}
                    : u.subscription_active ? {label:"PAGANDO", c:"#D4FF00"}
                    : u.subscription_status === "pix_pending_review" ? {label:"PIX PENDENTE", c:"#FFCE00"}
                    : u.subscription_status === "pix_rejected" ? {label:"REJEITADO", c:"#FF2A6D"}
                    : {label:"GRÁTIS", c:"#888"};
                  return (
                    <tr key={u.id} data-testid={`admin-user-${u.id}`} className="border-t border-white/[0.05]">
                      <td className="px-4 py-3 text-white/60 whitespace-nowrap">
                        {u.created_at ? new Date(u.created_at).toLocaleString('pt-BR', {day:'2-digit',month:'2-digit',hour:'2-digit',minute:'2-digit'}) : "—"}
                      </td>
                      <td className="px-4 py-3">
                        <div className="font-semibold text-white/90">{u.name || "(sem nome)"}</div>
                        <div className="text-white/50 text-xs">{u.email}</div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="inline-block px-2 py-0.5 border text-[10px] uppercase tracking-widest font-bold" style={{color:status.c, borderColor:status.c}}>
                          {status.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-white/50">{u.is_admin ? "👑" : "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Filters */}
        <div className="mt-10 brutal-card" style={{borderColor:'#00F0FF'}}>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <Film className="text-[#00F0FF]" size={22}/>
              <div>
                <div className="font-bebas text-2xl tracking-tighter">CAPAS EM VÍDEO IA</div>
                <div className="font-manrope text-xs text-white/50">Sora 2 · 8s por curso · ~2-5 min cada · fica cacheado pra sempre</div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                data-testid="reload-videos"
                onClick={loadAll}
                className="px-3 py-2 border-2 border-white/30 hover:border-white font-manrope text-xs uppercase tracking-widest flex items-center gap-2"
              >
                <RefreshCw size={14}/> Atualizar
              </button>
              <button
                data-testid="generate-all-videos"
                onClick={generateAllVideos}
                disabled={videoBusy}
                className="brutal-btn text-sm py-2 px-4 inline-flex items-center gap-2"
                style={{borderColor:'#00F0FF', background:'#00F0FF', color:'#050505'}}
              >
                {videoBusy ? <Loader2 className="animate-spin" size={16}/> : <Film size={16}/>}
                Gerar todas as capas
              </button>
            </div>
          </div>
          {videos.length > 0 && (
            <div className="mt-4 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2">
              {videos.map(v => {
                const c = v.status === "ready" ? "#CCFF00" : v.status === "generating" ? "#FFCE00" : "#FF0055";
                return (
                  <div key={v.course_id} data-testid={`video-status-${v.course_id}`} className="border-2 p-2 font-manrope text-xs" style={{borderColor: c}}>
                    <div className="font-bebas text-sm truncate" style={{color:c}}>{v.course_id}</div>
                    <div className="text-white/60 uppercase tracking-widest text-[10px] mt-1 flex items-center gap-1">
                      {v.status === "generating" && <Loader2 className="animate-spin" size={10}/>}
                      {v.status}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Filters */}
        <div className="mt-10 flex flex-wrap items-center gap-3">
          {["pending_review","confirmed","rejected","all"].map(s => (
            <button
              key={s}
              data-testid={`filter-${s}`}
              onClick={()=>setStatusFilter(s)}
              className={`px-4 py-2 font-bebas text-sm border-2 tracking-widest ${statusFilter===s ? "bg-[#CCFF00] text-black border-[#CCFF00]" : "text-white/70 border-white/30 hover:border-white"}`}
            >
              {s === "all" ? "Todos" : (STATUS_LABEL[s]?.label || s)}
            </button>
          ))}
          <div className="flex items-center gap-2 ml-auto">
            <Search size={16} className="text-white/40"/>
            <input
              data-testid="admin-search"
              value={search}
              onChange={e=>setSearch(e.target.value)}
              onKeyDown={e=> e.key==='Enter' && loadAll()}
              placeholder="Buscar email / nome"
              className="brutal-input"
              style={{padding:'0.5rem', width:250}}
            />
            <button onClick={loadAll} className="brutal-btn py-2 px-4 text-sm">Buscar</button>
          </div>
        </div>

        {/* Pix Table */}
        <div className="mt-6 space-y-3">
          {pix.length === 0 && (
            <div className="brutal-card text-white/50 font-manrope text-center py-10">
              Nenhum Pix nesse filtro.
            </div>
          )}
          {pix.map((p) => {
            const s = STATUS_LABEL[p.status] || { label: p.status, color: "#888", icon: null };
            return (
              <div key={p.id} data-testid={`admin-pix-${p.id}`} className="brutal-card">
                <div className="flex flex-wrap md:flex-nowrap items-start gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-bebas text-lg text-[#CCFF00]">R$ {p.amount.toFixed(2).replace('.',',')}</span>
                      <span className="font-manrope text-xs px-2 py-1 border-2 flex items-center gap-1" style={{color:s.color, borderColor:s.color}}>
                        {s.icon} {s.label}
                      </span>
                    </div>
                    <div className="mt-2 font-manrope text-sm">
                      <div><b className="text-white/90">Aluno:</b> <span className="text-white/70">{p.user_email}</span></div>
                      <div><b className="text-white/90">Nome no Pix:</b> <span className="text-white/70">{p.sender_name || "—"}</span></div>
                      <div className="text-white/40 text-xs mt-1">Enviado em {new Date(p.created_at).toLocaleString('pt-BR')}</div>
                      {p.reject_reason && <div className="text-[#FF0055] text-xs mt-1">Motivo: {p.reject_reason}</div>}
                      {p.reviewed_by && <div className="text-white/40 text-xs mt-1">Revisado por {p.reviewed_by}</div>}
                    </div>
                  </div>
                  <div className="flex flex-col gap-2 w-full md:w-auto md:min-w-[280px]">
                    {p.status === "pending_review" && (
                      <>
                        <button data-testid={`confirm-${p.id}`} onClick={()=>confirmPix(p.id)} disabled={busy===p.id} className="brutal-btn py-2 text-sm">
                          <CheckCircle2 size={16} className="mr-2"/> Confirmar (mantém 30d)
                        </button>
                        <input
                          data-testid={`reason-${p.id}`}
                          value={reasonInput[p.id] || ""}
                          onChange={e=>setReasonInput({...reasonInput, [p.id]: e.target.value})}
                          placeholder="Motivo da rejeição (opcional)"
                          className="brutal-input"
                          style={{padding:'0.4rem 0.6rem', fontSize:12}}
                        />
                        <button data-testid={`reject-${p.id}`} onClick={()=>rejectPix(p.id)} disabled={busy===p.id} className="brutal-btn py-2 text-sm" style={{background:'#FF0055', color:'#fff', boxShadow:'6px 6px 0 #050505'}}>
                          <XCircle size={16} className="mr-2"/> Rejeitar + Email
                        </button>
                      </>
                    )}
                    {p.status === "confirmed" && (
                      <button data-testid={`extend-${p.id}`} onClick={()=>extend(p.id)} disabled={busy===p.id} className="brutal-btn brutal-btn-outline py-2 text-sm">
                        <PlusCircle size={16} className="mr-2"/> +30 dias
                      </button>
                    )}
                    {p.status === "rejected" && (
                      <button data-testid={`reconfirm-${p.id}`} onClick={()=>confirmPix(p.id)} disabled={busy===p.id} className="brutal-btn py-2 text-sm">
                        Reverter (confirmar)
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
